(() => {
const EDULIT_AUTHORS_MOBILE_QUERY = '(max-width: 749px)';

function getAuthorPaginationState(totalItems, requestedPage, pageSize) {
  const safeTotalItems = Math.max(Math.trunc(Number(totalItems)) || 0, 0);
  const safePageSize = Math.max(Math.trunc(Number(pageSize)) || 1, 1);
  const pageCount = Math.ceil(safeTotalItems / safePageSize);
  const normalizedPage = Math.trunc(Number(requestedPage)) || 1;
  const currentPage = pageCount > 0 ? Math.min(Math.max(normalizedPage, 1), pageCount) : 1;
  const firstIndex = pageCount > 0 ? (currentPage - 1) * safePageSize : 0;

  return {
    currentPage,
    pageCount,
    firstIndex,
    lastIndex: Math.min(firstIndex + safePageSize, safeTotalItems),
  };
}

function getAuthorPageForIndex(index, pageSize) {
  const safeIndex = Math.max(Math.trunc(Number(index)) || 0, 0);
  const safePageSize = Math.max(Math.trunc(Number(pageSize)) || 1, 1);

  return Math.floor(safeIndex / safePageSize) + 1;
}

if (typeof customElements !== 'undefined' && !customElements.get('edulit-authors-listing')) {
  customElements.define(
    'edulit-authors-listing',
    class extends HTMLElement {
      connectedCallback() {
        const collator = new Intl.Collator('ro', { sensitivity: 'base' });
        this.cards = Array.from(this.querySelectorAll('[data-author-card]')).sort((firstCard, secondCard) => {
          const surnameOrder = collator.compare(
            firstCard.dataset.authorSortName || '',
            secondCard.dataset.authorSortName || '',
          );
          if (surnameOrder !== 0) return surnameOrder;

          return collator.compare(firstCard.textContent.trim(), secondCard.textContent.trim());
        });
        this.grid = this.querySelector('.edulit-authors-listing__grid');
        this.pagination = this.querySelector('[data-authors-pagination]');
        this.pageLinkTemplate = this.querySelector('[data-authors-page-link-template]');
        this.desktopPageSize = this.readPageSize(this.dataset.desktopPageSize, 40);
        this.mobilePageSize = this.readPageSize(this.dataset.mobilePageSize, 20);
        this.mobileMediaQuery = window.matchMedia(EDULIT_AUTHORS_MOBILE_QUERY);

        if (!this.grid) return;
        this.cards.forEach((card) => this.grid.appendChild(card));

        if (!this.pagination || !this.pageLinkTemplate) return;

        const requestedPage = Number(new URL(window.location.href).searchParams.get('authors_page')) || 1;
        const state = this.renderPage(requestedPage);

        if (state.currentPage !== Math.trunc(requestedPage)) {
          this.replacePageUrl(state.currentPage);
        }

        this.handleBreakpointChange = () => {
          const nextPage = getAuthorPageForIndex(this.firstIndex, this.getPageSize());
          this.renderPage(nextPage);
          this.replacePageUrl(nextPage);
        };

        if (this.mobileMediaQuery.addEventListener) {
          this.mobileMediaQuery.addEventListener('change', this.handleBreakpointChange);
        } else {
          this.mobileMediaQuery.addListener(this.handleBreakpointChange);
        }
      }

      disconnectedCallback() {
        if (!this.mobileMediaQuery || !this.handleBreakpointChange) return;

        if (this.mobileMediaQuery.removeEventListener) {
          this.mobileMediaQuery.removeEventListener('change', this.handleBreakpointChange);
        } else {
          this.mobileMediaQuery.removeListener(this.handleBreakpointChange);
        }
      }

      readPageSize(value, fallback) {
        const pageSize = Number(value);

        return Number.isInteger(pageSize) && pageSize > 0 ? pageSize : fallback;
      }

      getPageSize() {
        return this.mobileMediaQuery.matches ? this.mobilePageSize : this.desktopPageSize;
      }

      renderPage(page) {
        const state = getAuthorPaginationState(this.cards.length, page, this.getPageSize());

        this.firstIndex = state.firstIndex;
        this.cards.forEach((card, index) => {
          card.hidden = index < state.firstIndex || index >= state.lastIndex;
        });

        this.buildPageLinks(state.pageCount, state.currentPage);

        const summary = this.pagination.querySelector('[data-authors-page-summary]');
        if (summary && state.pageCount > 0) {
          summary.textContent = `Se afișează ${state.firstIndex + 1}–${state.lastIndex} din ${this.cards.length} de autori`;
        }

        this.updateArrow(
          this.pagination.querySelector('[data-authors-page-previous]'),
          state.currentPage - 1,
          state.currentPage === 1,
        );
        this.updateArrow(
          this.pagination.querySelector('[data-authors-page-next]'),
          state.currentPage + 1,
          state.currentPage === state.pageCount,
        );

        this.pagination.hidden = state.pageCount <= 1;

        return state;
      }

      buildPageLinks(pageCount, currentPage) {
        this.pagination.querySelectorAll('[data-authors-page-link-item]').forEach((item) => item.remove());

        const nextArrowItem = this.pagination.querySelector('[data-authors-page-next]')?.closest('li');
        if (!nextArrowItem) return;

        for (let page = 1; page <= pageCount; page += 1) {
          const fragment = this.pageLinkTemplate.content.cloneNode(true);
          const item = fragment.querySelector('li');
          const link = fragment.querySelector('[data-authors-page-link]');
          const isCurrent = page === currentPage;

          if (!item || !link) continue;

          item.dataset.authorsPageLinkItem = '';
          link.dataset.authorsPageLink = String(page);
          link.textContent = String(page);
          link.setAttribute('aria-label', `Pagina ${page}`);
          link.classList.toggle('pagination__item--current', isCurrent);
          link.classList.toggle('light', isCurrent);

          if (isCurrent) {
            link.setAttribute('aria-current', 'page');
            link.removeAttribute('href');
          } else {
            link.removeAttribute('aria-current');
            link.href = this.pageUrl(page);
          }

          nextArrowItem.before(fragment);
        }
      }

      replacePageUrl(page) {
        window.history.replaceState(null, '', this.pageUrl(page));
      }

      updateArrow(link, page, hidden) {
        if (!link) return;

        link.closest('li').hidden = hidden;
        if (hidden) {
          link.removeAttribute('href');
        } else {
          link.href = this.pageUrl(page);
        }
      }

      pageUrl(page) {
        const url = new URL(window.location.href);

        if (page === 1) {
          url.searchParams.delete('authors_page');
        } else {
          url.searchParams.set('authors_page', String(page));
        }

        url.hash = 'edulit-authors-listing';
        return `${url.pathname}${url.search}${url.hash}`;
      }
    },
  );
}

if (typeof customElements !== 'undefined' && !customElements.get('edulit-sorted-authors')) {
  customElements.define(
    'edulit-sorted-authors',
    class extends HTMLElement {
      connectedCallback() {
        const entries = Array.from(this.querySelectorAll('[data-author-sort-entry]'));
        const parent = entries[0]?.parentElement;

        if (!parent || entries.some((entry) => entry.parentElement !== parent)) return;

        const collator = new Intl.Collator('ro', { sensitivity: 'base' });
        entries.sort((firstEntry, secondEntry) => {
          const surnameOrder = collator.compare(
            firstEntry.dataset.authorSortName || '',
            secondEntry.dataset.authorSortName || '',
          );
          if (surnameOrder !== 0) return surnameOrder;

          return collator.compare(firstEntry.textContent.trim(), secondEntry.textContent.trim());
        });

        entries.forEach((entry, index) => {
          parent.appendChild(entry);
          const separator = entry.querySelector('[data-author-sort-separator]');
          if (separator) separator.textContent = index === 0 ? '' : ', ';
        });
      }
    },
  );
}

if (typeof module !== 'undefined') {
  module.exports = { getAuthorPaginationState, getAuthorPageForIndex };
}
})();
